import { lazy, Suspense } from 'react'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import AppShell from './layout/AppShell'
import Home from './routes/Home'
import LeakTest from './routes/LeakTest'
import DohSpeed from './routes/DohSpeed'
import SharedResult from './routes/SharedResult'
import NotFound from './routes/NotFound'
import RouteSeo from './seo/RouteSeo'
import ThirdPartyScripts from './features/ads/ThirdPartyScripts'

// long-form pages are not on the critical path and carry the most prose
const FixingDnsLeak = lazy(() => import('./routes/FixingDnsLeak'))
const PrivacyPolicy = lazy(() => import('./routes/PrivacyPolicy'))
// the resolver directory pulls in charts, a table and the filter logic
const DohResolvers = lazy(() => import('./routes/DohResolvers'))
// fifty-odd answers, none of them needed on the critical path
const Faq = lazy(() => import('./routes/Faq'))

export function createQueryClient() {
    return new QueryClient({
        defaultOptions: {
            queries: {
                // this data is about the visitor's own network; refetching on tab
                // focus would silently change results while they are reading them
                refetchOnWindowFocus: false,
                retry: 1,
            },
        },
    })
}

const defaultClient = createQueryClient()

interface AppProps {
    /** Injectable so tests can disable retries and assert error states. */
    queryClient?: QueryClient
}

export default function App({ queryClient = defaultClient }: AppProps = {}) {
    return (
        <QueryClientProvider client={queryClient}>
            {/* The router must know the prefix it is mounted under. In production
                BASE_URL is '/', but the staging mount at /v2/ builds with
                BASE=/v2/ -- without this every route there falls through to the
                404, while nginx still answers 200 because it is serving the SPA
                shell perfectly well. Status codes cannot catch that. */}
            <BrowserRouter basename={import.meta.env.BASE_URL}>
                <RouteSeo />
                <ThirdPartyScripts />
                <Routes>
                    <Route element={<AppShell />}>
                        <Route index element={<Home />} />
                        <Route path="dns-leak-test" element={<LeakTest />} />
                        <Route path="dns-leak-test/:id" element={<SharedResult />} />
                        <Route path="dns-over-https" element={<DohSpeed />} />
                        <Route
                            path="doh-resolvers"
                            element={<Suspense fallback={<p>Loading…</p>}><DohResolvers /></Suspense>}
                        />
                        <Route
                            path="faq"
                            element={<Suspense fallback={<p>Loading…</p>}><Faq /></Suspense>}
                        />
                        <Route
                            path="fixing-dns-leak"
                            element={<Suspense fallback={<p>Loading…</p>}><FixingDnsLeak /></Suspense>}
                        />
                        <Route
                            path="privacy-policy"
                            element={<Suspense fallback={<p>Loading…</p>}><PrivacyPolicy /></Suspense>}
                        />
                        <Route path="*" element={<NotFound />} />
                    </Route>
                </Routes>
            </BrowserRouter>
        </QueryClientProvider>
    )
}
